/**
 * MyGroupsTab — a preview carousel of the member's groups (with a View All
 * escape hatch to the full list) above the feed of posts from those groups
 * (Figma 3463-1808). Search and sort live on the All Groups tab and the
 * dedicated My Groups list screen, matching mobile.
 */
import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGroup } from '@wlb/shared/context/GroupContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { GROUP_STATUS } from '@wlb/shared/constants';
import { MY_GROUPS_PREVIEW_LIMIT } from '@wlb/shared/utils/filterGroups';
import type { Group } from '@wlb/shared/types/groups';
import { ArrowRightIcon } from '../../components/ui/icons';
import { GroupPostsFeed } from './GroupPostsFeed';
import { GroupTile } from './GroupTile';
import { GroupTilesSkeleton } from './GroupSkeletons';
import { useScrollRestore } from './useScrollRestore';
import '../../components/ui/skeleton.css';
import '../feed/feed.css';
import './groups.css';

export function MyGroupsTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    myGroups,
    groupPosts,
    myGroupsLoading,
    groupPostsLoading,
    myGroupsError,
    groupPostsError,
    myGroupsLoaded,
    groupPostsLoaded,
    fetchMyGroups,
    fetchGroupPosts,
  } = useGroup();

  const load = useCallback(() => {
    fetchMyGroups().catch(() => undefined);
    fetchGroupPosts().catch(() => undefined);
  }, [fetchMyGroups, fetchGroupPosts]);

  useEffect(load, [load]);

  /** Member-initiated reload of both lists this tab is built from. */
  const refresh = useCallback(() => {
    fetchMyGroups(undefined, { force: true }).catch(() => undefined);
    fetchGroupPosts(null, { force: true }).catch(() => undefined);
  }, [fetchMyGroups, fetchGroupPosts]);

  const previewGroups = useMemo(
    () => myGroups.slice(0, MY_GROUPS_PREVIEW_LIMIT),
    [myGroups],
  );

  // Mobile keeps this feed to groups the member actually belongs to; the shared
  // group post list can also carry public groups they only browse.
  const joinedGroupIds = useMemo(
    () =>
      new Set(
        myGroups
          .filter(
            group =>
              group.status === GROUP_STATUS.JOINED ||
              group.status === GROUP_STATUS.OWNER ||
              group.isOwner ||
              group.isAdmin,
          )
          .map(group => String(group.id)),
      ),
    [myGroups],
  );

  const joinedGroupPosts = useMemo(
    () => groupPosts.filter(post => post.groupId != null && joinedGroupIds.has(String(post.groupId))),
    [groupPosts, joinedGroupIds],
  );

  const listReady = !(myGroupsLoading || groupPostsLoading) || joinedGroupPosts.length > 0;
  useScrollRestore('groups:myGroups', listReady);

  const handleGroupSelect = (group: Group) => navigate(`/groups/${group.id}`);

  const emptyMessage = myGroups.length === 0
    ? t('home.myGroups.emptyGroups')
    : t('home.myGroups.emptyPosts');

  return (
    <>
      {myGroupsLoading && myGroups.length === 0 && (
        <section className="my-groups__header">
          <GroupTilesSkeleton />
        </section>
      )}

      {myGroups.length > 0 && (
        <section className="my-groups__header skeleton-fade-in">
          <h2 className="my-groups__title">{t('home.groupTabs.myGroups')}</h2>
          <div className="my-groups__carousel">
            {previewGroups.map(group => (
              <GroupTile key={group.id} group={group} onSelect={handleGroupSelect} />
            ))}
            {myGroups.length > MY_GROUPS_PREVIEW_LIMIT && (
              <button
                type="button"
                className="my-groups__view-all"
                onClick={() => navigate('/groups/my')}
              >
                <span>{t('home.myGroups.viewAll')}</span>
                <ArrowRightIcon size={16} />
              </button>
            )}
          </div>
        </section>
      )}

      <GroupPostsFeed
        posts={joinedGroupPosts}
        loading={myGroupsLoading || groupPostsLoading}
        // This tab is built from both lists, so either failure is a failure here.
        error={groupPostsError ?? myGroupsError}
        loaded={myGroupsLoaded && groupPostsLoaded}
        onRetry={refresh}
        emptyMessage={emptyMessage}
      />
    </>
  );
}
